import React from 'react'

export type AccordionItemType = {
  value?: string
  isOpen?: boolean
  state?: 'open' | 'closed'
  triggerId?: string
  labelledId?: string
  ref?: null | React.RefObject<HTMLElement>
  level?: number
  childrenValue?: string[]
  parentValue?: string
  disabled?: boolean
  startOpen?: boolean
}

type AccordionActionPayload = {
  childValue?: string
  collapsible?: boolean
}

export type AccordionAction = {
  type: string
  payload: AccordionItemType & AccordionActionPayload
}

export type AccordionState = {
  items: AccordionItemType[]
}

export const initialState = {
  items: [],
}

const getChildren = (item: AccordionItemType, items: AccordionItemType[] = []) => {
  const children: AccordionItemType[] = []

  const get = (currentItem: AccordionItemType, currentItems: AccordionItemType[] = []) => {
    if (!currentItem.childrenValue?.length || !currentItem?.isOpen) {
      return
    }

    currentItem.childrenValue.forEach((itemValue) => {
      const child = items.find((currentChild) => currentChild.value === itemValue)

      if (child) {
        children.push(child)
        get(child, currentItems)
      }
    })
  }

  get(item, items)

  return [...children, item].map((currentItem) => currentItem.value)
}

const getParents = (item: AccordionItemType, items: AccordionItemType[] = []) => {
  const parents: AccordionItemType[] = []

  const get = (currentItem: AccordionItemType, currentItems: AccordionItemType[] = []) => {
    if (!currentItem.parentValue) {
      return
    }

    const currentParentItem = currentItems.find((child) => child.value === currentItem.parentValue)

    if (currentParentItem) {
      parents.push(currentParentItem)
      get(currentParentItem, currentItems)
    }
  }

  get(item, items)

  return parents.map((currentItem) => currentItem.value)
}

export const accordionReducer = ((state, { type, payload = {} }) => {
  const { items = [] } = state

  switch (type) {
    case 'ADD_ITEM': {
      return {
        items: [...items, payload],
      }
    }

    case 'TOGGLE_ITEM': {
      const currentItem = items.find((item) => item.value === payload.value)
      const children = getChildren(currentItem || {}, items)
      const itemsToToggle = [currentItem?.value, ...children]

      return {
        items: items.map((item) => {
          return {
            ...item,
            isOpen:
              currentItem?.isOpen && itemsToToggle.indexOf(item.value) > -1
                ? false
                : itemsToToggle.indexOf(item.value) > -1
                ? !item.isOpen
                : item.isOpen,
          }
        }),
      }
    }

    case 'TOGGLE_SINGLE_ITEM': {
      return {
        items: items.map((item) => {
          return {
            ...item,
            isOpen:
              payload.collapsible && payload.value === item.value
                ? !item.isOpen
                : payload.value === item.value,
          }
        }),
      }
    }

    case 'OPEN_SELECTED': {
      const currentItem = items.find((item) => item.value === payload.value)
      const parents = getParents(currentItem || {}, items)
      const itemsToOpen = [currentItem?.value, ...parents]

      return {
        items: items.map((item) => {
          return {
            ...item,
            isOpen: itemsToOpen.indexOf(item.value) > -1 ? true : item.isOpen,
          }
        }),
      }
    }

    case 'ADD_CHILDREN': {
      return {
        items: items.map((item) => ({
          ...item,
          childrenValue:
            item.value === payload.value
              ? [...(item.childrenValue as string[]), payload.childValue]
              : [...(item.childrenValue as string[])],
        })),
      }
    }

    default:
      return state
  }
}) as React.Reducer<AccordionState, AccordionAction>
